import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';

import type { ApiError } from '../@types';
import type {
  FeedTemplateSwitchOperation,
  FeedTemplateSwitchState,
  FeedTemplateTarget,
  SuperAdminActiveTemplateResponse,
} from '../@types/api';
import { QUERY_KEYS } from '../constants';
import API_ROUTES from '../constants/apiRoutes';
import { getData, postData } from '../utils/api';

const PENDING_OPERATION_KEY = 'feed-template-switch:v1:pending';
const readPendingId = () => {
  try {
    const id = sessionStorage.getItem(PENDING_OPERATION_KEY);
    return id && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id) ? id : null;
  } catch {
    return null;
  }
};

export const useFeedTemplateSwitch = () => {
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState(readPendingId);
  const [requestUncertain, setRequestUncertain] = useState(false);
  const [startConfirmed, setStartConfirmed] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const requestId = useRef(pendingId);
  const confirmedRequestId = useRef<string | null>(null);

  const clearPendingRequest = useCallback((id: string | null) => {
    if (!id || requestId.current !== id) return;
    try {
      sessionStorage.removeItem(PENDING_OPERATION_KEY);
    } catch {
      // Storage is optional; recovery still uses the backend operation ID.
    }
    setPendingId(null);
    setRequestUncertain(false);
  }, []);

  const confirmRecovery = useCallback(
    (state: FeedTemplateSwitchState, id: string) => {
      if (state.operation?.id !== id || requestId.current !== id) return;
      confirmedRequestId.current = id;
      setStartConfirmed(true);
      const latest = queryClient.getQueryData<FeedTemplateSwitchState>([QUERY_KEYS.superAdminTemplateSwitch]);
      if (!latest?.locked || latest.operation?.id === id)
        queryClient.setQueryData([QUERY_KEYS.superAdminTemplateSwitch], state);
      clearPendingRequest(id);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminTemplateSwitch] });
    },
    [queryClient, clearPendingRequest]
  );

  const latest = useQuery<FeedTemplateSwitchState, ApiError>({
    queryKey: [QUERY_KEYS.superAdminTemplateSwitch],
    queryFn: () => getData(API_ROUTES.BO_TEMPLATES_SWITCH, undefined, 'ftbConsumer'),
    refetchInterval: (query) => (query.state.data?.operation?.status === 'running' ? 2_000 : false),
    retry: 1,
    retryDelay: 250,
  });
  const pending = useQuery<FeedTemplateSwitchState, ApiError>({
    queryKey: [QUERY_KEYS.superAdminTemplateSwitch, pendingId],
    queryFn: () => getData(`${API_ROUTES.BO_TEMPLATES_SWITCH}/${pendingId}`, undefined, 'ftbConsumer'),
    enabled: Boolean(pendingId),
    refetchInterval: pendingId ? 2_000 : false,
    retry: 1,
    retryDelay: 250,
  });

  useEffect(() => {
    if (pendingId && pending.data) confirmRecovery(pending.data, pendingId);
  }, [pendingId, pending.data, confirmRecovery]);

  const active = useQuery<SuperAdminActiveTemplateResponse, ApiError>({
    queryKey: [QUERY_KEYS.superAdminActiveTemplate],
    queryFn: () => getData(API_ROUTES.BO_TEMPLATES_ACTIVE, undefined, 'ftbConsumer'),
    retry: 1,
    retryDelay: 250,
  });

  const mutation = useMutation<FeedTemplateSwitchOperation, ApiError, FeedTemplateTarget>({
    mutationFn: (target) => {
      setStartError(null);
      const id = crypto.randomUUID();
      requestId.current = id;
      confirmedRequestId.current = null;
      setStartConfirmed(false);
      try {
        sessionStorage.setItem(PENDING_OPERATION_KEY, id);
      } catch {
        /* Server latest still permits refresh recovery. */
      }
      setPendingId(id);
      return postData(`${API_ROUTES.BO_TEMPLATES_SWITCH}/${target}`, { operationId: id }, 'ftbConsumer');
    },
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminTemplateSwitch] });
    },
    onError: (error) => {
      if (confirmedRequestId.current === requestId.current) return;
      const recovered = queryClient.getQueryData<FeedTemplateSwitchState>([QUERY_KEYS.superAdminTemplateSwitch]);
      if (recovered?.operation?.id === requestId.current) {
        confirmRecovery(recovered, requestId.current);
        return;
      }

      // A definite validation/conflict response means this request did not start a switch.
      if (isAxiosError(error) && error.response && error.response.status >= 400 && error.response.status < 500) {
        setStartError(
          error.response.status === 409
            ? 'Another manual switch is running or requires reconciliation. Refresh status to review it.'
            : `The backend rejected the switch request (HTTP ${error.response.status}). Refresh status before trying again.`
        );
        clearPendingRequest(requestId.current);
      } else setRequestUncertain(true);
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminTemplateSwitch] });
    },
  });

  const operation = pending.data?.operation ?? latest.data?.operation ?? mutation.data ?? null;
  const finishedAt = operation?.finishedAt;
  useEffect(() => {
    if (finishedAt) void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminActiveTemplate] });
  }, [finishedAt, queryClient]);

  const release = useMutation<void, ApiError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      postData(`${API_ROUTES.BO_TEMPLATES_SWITCH}/${id}/release`, { reason }, 'ftbConsumer'),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminTemplateSwitch] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminActiveTemplate] });
    },
  });

  const statusUnavailable = latest.isError || Boolean(pendingId && pending.isError) || requestUncertain;
  const switchStatusUnavailable = latest.isPending || statusUnavailable;
  const requestOutstanding = Boolean(pendingId) || (mutation.isPending && !startConfirmed);
  // A retained lock blocks new switches; recovery flags and snapshots remain available.
  const controlsBlocked = latest.isPending || operation?.status === 'running' || requestOutstanding;
  const disabled = switchStatusUnavailable || Boolean(latest.data?.locked) || controlsBlocked || release.isPending;
  const canRelease = Boolean(
    latest.data?.locked && operation && ['failed', 'interrupted'].includes(operation.status) && !statusUnavailable
  );

  return {
    operation,
    controlsBlocked,
    canRelease,
    release: release.mutateAsync,
    releasePending: release.isPending,
    releaseError: release.isError,
    feedRunning: latest.data?.feedRunning ?? null,
    activeTemplate: active.isError ? null : (active.data?.activeTemplate ?? null),
    disabled: Boolean(disabled),
    statusUnavailable,
    startError,
    isLoading: latest.isPending,
    start: mutation.mutateAsync,
    refresh: () => {
      void latest.refetch();
      void active.refetch();
      if (pendingId) void pending.refetch();
    },
  };
};
